import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  useGetAccountsByPlatformQuery,
  useGetPlatformsQuery,
} from 'src/redux/admin/reporting/reporting.endpoints';
import { AccountType } from 'src/redux/admin/reporting/reporting.types';
import { formatPlatformLabel } from 'src/pages/reports/utils/format-reporting-labels';
import { BetaFilterSelectSkeleton } from './beta-charts/beta-chart-skeletons';
import { MartReportFilterSelect } from './mart-report-filter-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { cn } from 'src/lib/utils';

export interface FlightAccountOption {
  id: string | number;
  name: string;
}

export interface FlightsFilterSelection {
  source: string;
  accounts: FlightAccountOption[];
}

interface FlightsFilterBarProps {
  onSelectionChange: (selection: FlightsFilterSelection) => void;
}

const FlightsFilterBar = ({ onSelectionChange }: FlightsFilterBarProps) => {
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<FlightAccountOption[]>([]);
  const autoSelectedForSource = useRef('');
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const { data: platforms = [], isLoading: platformsLoading } = useGetPlatformsQuery({});
  const { data: accounts = [], isFetching: accountsFetching } = useGetAccountsByPlatformQuery(
    { source: selectedSource },
    { skip: !selectedSource }
  );

  useEffect(() => {
    if (!selectedSource || accountsFetching || accounts.length === 0) {
      return;
    }
    if (autoSelectedForSource.current === selectedSource) {
      return;
    }
    autoSelectedForSource.current = selectedSource;
    setSelectedAccounts(accounts.map(toAccountOption));
  }, [selectedSource, accounts, accountsFetching]);

  useEffect(() => {
    onSelectionChangeRef.current({
      source: selectedSource,
      accounts: selectedAccounts,
    });
  }, [selectedSource, selectedAccounts]);

  const accountLabel = (() => {
    if (!selectedSource) return 'Select platform first';
    if (accountsFetching && selectedAccounts.length === 0) return 'Loading accounts';
    if (selectedAccounts.length === 0) return 'Select accounts';
    if (accounts.length > 0 && selectedAccounts.length === accounts.length) {
      return 'All accounts';
    }
    if (selectedAccounts.length === 1) return selectedAccounts[0].name;
    return `${selectedAccounts.length} accounts`;
  })();

  const toggleAccount = (account: FlightAccountOption) => {
    setSelectedAccounts((current) => {
      if (current.some((item) => String(item.id) === String(account.id))) {
        return current.filter((item) => String(item.id) !== String(account.id));
      }
      return [...current, account];
    });
  };

  return (
    <div className="mb-6 rounded-xl border border-[#E8E9ED] bg-white p-4 shadow-[0_1px_3px_rgba(13,15,26,0.04)]">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div className="min-w-0">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
            Platform
          </label>
          {platformsLoading ? (
            <BetaFilterSelectSkeleton />
          ) : (
            <MartReportFilterSelect
              aria-label="Platform"
              value={selectedSource}
              active={Boolean(selectedSource)}
              onChange={(value) => {
                autoSelectedForSource.current = '';
                setSelectedSource(value);
                setSelectedAccounts([]);
              }}
            >
              <option value="">
                Select platform
              </option>
              {platforms.map((platform) => (
                <option
                  key={platform}
                  value={platform}
                >
                  {formatPlatformLabel(platform)}
                </option>
              ))}
            </MartReportFilterSelect>
          )}
        </div>

        <div className="min-w-0">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
            Accounts
          </label>
          {platformsLoading || (selectedSource && accountsFetching && selectedAccounts.length === 0) ? (
            <BetaFilterSelectSkeleton />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                disabled={!selectedSource}
              >
                <button
                  type="button"
                  aria-label="Accounts"
                  disabled={!selectedSource}
                  className={cn(
                    'flex h-[38px] w-full min-w-0 items-center justify-between rounded-lg border-[1.5px] bg-white px-3 text-left text-[13px] font-semibold transition-colors',
                    selectedAccounts.length > 0
                      ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]'
                      : 'border-[#D9DBE3] text-[#5E6278] hover:border-[#B8BBCA] hover:text-[#0D0F1A]',
                    !selectedSource && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <span className="truncate">
                    {accountLabel}
                  </span>
                  <ChevronDown
                    aria-hidden
                    strokeWidth={2.5}
                    className={cn(
                      'ml-2 h-[13px] w-[13px] shrink-0',
                      selectedAccounts.length > 0 ? 'text-[#2563EB]' : 'text-[#8A8EA0]'
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[min(420px,calc(100vw-3rem))] p-0"
              >
                <div className="flex border-b border-[#EDEEF2] text-[12px] font-semibold text-[#5E6278]">
                  <button
                    type="button"
                    className="flex-1 px-3 py-2 text-left hover:bg-[#F0F1F5]"
                    onClick={() => setSelectedAccounts(accounts.map(toAccountOption))}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="flex-1 border-l border-[#EDEEF2] px-3 py-2 text-left hover:bg-[#F0F1F5]"
                    onClick={() => setSelectedAccounts([])}
                  >
                    Select none
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {accounts.map((account) => {
                    const selected = selectedAccounts.some(
                      (item) => String(item.id) === String(account.id)
                    );
                    return (
                      <button
                        key={String(account.id)}
                        type="button"
                        className={cn(
                          'flex w-full items-start gap-2 px-3 py-2 text-left text-[13px] hover:bg-[#F0F1F5]',
                          selected ? 'text-[#0D0F1A]' : 'text-[#5E6278]'
                        )}
                        onClick={() => toggleAccount(toAccountOption(account))}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            selected
                              ? 'border-[#2563EB] bg-[#2563EB] text-white'
                              : 'border-[#D9DBE3] bg-white'
                          )}
                        >
                          {selected ? (
                            <Check
                              className="h-3 w-3"
                              strokeWidth={3}
                            />
                          ) : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">
                            {account.name}
                          </span>
                          <span className="block truncate text-[11px] text-[#9CA3AF]">
                            {account.id}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
};

function toAccountOption(account: AccountType): FlightAccountOption {
  return { id: account.id, name: account.name };
}

export default FlightsFilterBar;
